// src/components/ContentFocusDashboardModal.js

import React, { useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './HealthDashboardModal.module.css'; // Reusing existing complete CSS
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCrosshairs,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types'; // Recommended for production-ready components

const ContentFocusDashboardModal = ({
  isOpen,
  onClose,
  data,
  isLoading,
  tierClassName,
}) => {
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // --- REFACTOR START ---
  // Destructure all possible data points, including the new failure_reason
  const score = data?.contentFocusScore ?? 0;
  const tier = data?.focusTier ?? 'N/A';
  const summary = data?.channel_summary ?? 'Summary not available.';
  const failureReason = data?.failure_reason;
  // --- REFACTOR END ---

  /**
   * Renders the main content of the modal based on the current state.
   * This now includes a dedicated view for when a failure_reason is present.
   */
  const renderModalContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading Content Focus Analysis...</p>
        </div>
      );
    }

    if (!data) {
      return (
        <div className={styles.errorContainer}>
          <h4>Analysis Not Available</h4>
          <p>Content Focus data could not be generated for this channel.</p>
        </div>
      );
    }

    // --- REFACTOR START ---
    // If a failure_reason exists, we display a specific error message
    // instead of the dashboard. This is the core of the new logic.
    if (failureReason) {
      return (
        <div className={styles.rationaleCard} style={{ gridColumn: '1 / -1' }}>
          <h4>
            <FontAwesomeIcon icon={faExclamationTriangle} /> Analysis Incomplete
          </h4>
          <div className={styles.markdownContent}>
            <p>
              The content focus analysis could not be completed for the
              following reason:
            </p>
            <p className={styles.failureReasonText}>
              <strong>{failureReason}</strong>
            </p>
          </div>
        </div>
      );
    }
    // --- REFACTOR END ---
    console.log('Received tierClassName prop in modal:', tierClassName);
    // If no failure_reason is found, render the original success view.
    return (
      <div className={styles.mainGrid}>
        <div className={styles.scoreCard}>
          <div className={`${styles.tierBadge} ${tierClassName}`}>{tier}</div>
          <div className={styles.scoreValue}>{score.toFixed(1)}</div>
          <div className={styles.scoreLabel}>Content Focus Score</div>
        </div>
        <div className={styles.rationaleCard}>
          <h4>
            <FontAwesomeIcon icon={faCrosshairs} /> AI Channel Summary
          </h4>
          <div className={styles.markdownContent}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children, ...props }) => (
                  <p
                    style={{
                      fontSize: '1em',
                      marginTop: '10px',
                      marginBottom: '10px',
                      lineHeight: '1.5',
                      color: '#c0c0c0',
                    }}
                    {...props}
                  >
                    {children}
                  </p>
                ),
                h3: ({ children, ...props }) => (
                  <h3
                    style={{
                      fontSize: '1.1em',
                      fontWeight: 'bold',
                      marginTop: '15px',
                      borderBottom: '1px solid #444',
                    }}
                    {...props}
                  >
                    {children}
                  </h3>
                ),
                strong: ({ children, ...props }) => (
                  <strong style={{ color: '#ffffff' }} {...props}>
                    {children}
                  </strong>
                ),
              }}
            >
              {summary}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Content Focus Analysis</h3>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        {renderModalContent()}
      </div>
    </div>
  );
};

// PropTypes help ensure the component receives the correct data types,
// making it more robust and easier to debug.
ContentFocusDashboardModal.propTypes = {
  /** Controls if the modal is visible */
  isOpen: PropTypes.bool.isRequired,
  /** Function to call when the modal should be closed */
  onClose: PropTypes.func.isRequired,
  /** The dashboard data object from the context/API */
  data: PropTypes.shape({
    focusTier: PropTypes.string,
    contentFocusScore: PropTypes.number,
    channel_summary: PropTypes.string,
    /** If this key exists, the modal will display the failure reason instead of the dashboard */
    failure_reason: PropTypes.string,
  }),
  /** Indicates if the data is currently being fetched */
  isLoading: PropTypes.bool,
  /** A dynamic CSS class name for styling the tier badge (e.g., styles.tierPrime) */
  tierClassName: PropTypes.string,
};

ContentFocusDashboardModal.defaultProps = {
  data: null,
  isLoading: false,
  tierClassName: '',
};

export default ContentFocusDashboardModal;
